import React from "react";
import { Check, ExternalLink, CalendarCheck, Sun, Leaf } from "lucide-react";

const scrollToForm = () => {
  const element = document.getElementById("contact-form");
  if (element) element.scrollIntoView({ behavior: "smooth" });
};

const PricingSectionEN = () => {
  const seasons = [
    {
      season: "High season",
      period: "1 May – 30 Aug",
      price: "€145",
      icon: Sun,
      accent: "from-amber-50 to-orange-50 border-orange-200",
      badge: "bg-orange-100 text-orange-800",
      highlight: true,
    },
    {
      season: "Low season",
      period: "Rest of the year",
      price: "€120",
      icon: Leaf,
      accent: "from-green-50 to-emerald-50 border-green-200",
      badge: "bg-green-100 text-green-800",
      highlight: false,
    },
  ];

  const conditions = [
    "Bed linen included",
    "Beds made on arrival",
    "Mandatory cleaning fee: €70",
    "Towels: €6/person for the whole stay (2 towels/person)",
    "Minimum stay: 2 nights",
  ];

  return (
    <section
      id="pricing"
      className="section-padding bg-[#EDE6DB]/20"
      aria-labelledby="pricing-title"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2
            id="pricing-title"
            className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            Our rates
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            Simple, transparent pricing for your stay at Cœurs de Normands.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-10">
          {seasons.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className={`relative bg-gradient-to-br ${s.accent} border rounded-2xl p-8 shadow-lg ${s.highlight ? "ring-2 ring-orange-300" : ""}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-white/70 flex items-center justify-center">
                    <Icon size={24} className="text-[#1C3A27]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl font-bold text-[#1C3A27]">
                      {s.season}
                    </h3>
                    <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${s.badge}`}>
                      {s.period}
                    </span>
                  </div>
                </div>
                <div className="mt-6">
                  <div className="flex items-end gap-2">
                    <span className="font-serif text-4xl md:text-5xl font-bold text-[#1C3A27]">
                      {s.price}
                    </span>
                    <span className="text-[#1C3A27]/70 mb-1">/ night</span>
                  </div>
                  <p className="text-[#1C3A27]/80 mt-1">up to 4 guests</p>
                  <p className="text-sm text-[#1C3A27]/70 mt-3 flex items-center gap-2">
                    <Check size={16} className="text-green-600 flex-shrink-0" />
                    <span>
                      <strong>+€18</strong> / extra guest / night
                      <span className="block text-xs text-[#1C3A27]/60">from the 5th guest</span>
                    </span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#EDE6DB]/50 mb-10">
          <h3 className="font-serif text-lg md:text-xl font-bold text-[#1C3A27] mb-5 text-center">
            Good to know
          </h3>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl mx-auto">
            {conditions.map((c, i) => (
              <li key={i} className="flex items-start gap-3 text-[#1C3A27]/80">
                <Check size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <p className="text-center text-xs text-[#1C3A27]/60 mt-6">
            Open all year • Payment by cheque, cash or bank transfer
          </p>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-br from-[#1C3A27] to-[#2A5236] rounded-2xl p-8 md:p-10 text-white max-w-4xl mx-auto">
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3">
              Ready to come?
            </h3>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Tell us your dates: we'll check availability and get back to you quickly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={scrollToForm}
                className="bg-white text-[#1C3A27] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center w-full sm:w-auto min-h-[48px]"
                aria-label="Check my dates - go to the enquiry form"
              >
                <CalendarCheck size={20} className="mr-2" />
                Check my dates
              </button>
              <a
                href="https://www.airbnb.fr/rooms/1124426465764074190"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1C3A27] font-semibold px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center w-full sm:w-auto min-h-[48px]"
              >
                <ExternalLink size={20} className="mr-2" />
                Airbnb
              </a>
              <a
                href="https://www.booking.com/hotel/fr/gite-au-coeur-de-la-suisse-normande.fr.html"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1C3A27] font-semibold px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center w-full sm:w-auto min-h-[48px]"
              >
                <ExternalLink size={20} className="mr-2" />
                Booking
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSectionEN;
